import { useEffect } from "react";
import { TabData } from "./ScrollableTabs";

/**
 * useTopProximitySpy
 * Watches scroll position and sets the active tab index to the section whose top is closest to the top of the viewport (minus header offset).
 *
 * @param {Array} tabsData - Array of tab data objects, each with a .elementRef property (React ref to section DOM node)
 * @param {Function} setSelectedIndex - Setter for the active tab index
 * @param {number} headerOffset - Total height in px of sticky headers at the top (default: 155)
 */
function useTopProximitySpy(
  tabsData: TabData[], 
  setSelectedIndex: (index: number) => void, 
  headerOffset = 155, 
  suppressObserverRef : React.RefObject<boolean>,
  scrollContainerRef?: React.RefObject<HTMLElement>,
) {
  useEffect(() => {
    function onScroll() {
       // Only update if not suppressed
       if (suppressObserverRef?.current) return;

      let closestIdx = 0;
      let minDistance = Infinity;

      // If scrollContainerRef is provided and current, use its top for relative calculations
      const containerTop = scrollContainerRef?.current
        ? scrollContainerRef.current.getBoundingClientRect().top
        : 0;

      tabsData.forEach((tab, idx) => {
        const el = tab.elementRef?.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        // If using a scroll container, calculate position relative to it
        const relativeTop = rect.top - containerTop;
        const distance = Math.abs(relativeTop - headerOffset);
        if (relativeTop - headerOffset <= 0 && distance < minDistance) {
          minDistance = distance;
          closestIdx = idx;
        }
      });

      setSelectedIndex(closestIdx);
    }

    const scrollEl = scrollContainerRef?.current || window;
    // Listen to scroll events
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    // Run once on mount (in case already scrolled)
    onScroll();
    // Cleanup on unmount
    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, [tabsData, setSelectedIndex, headerOffset, suppressObserverRef, scrollContainerRef]);
}

export default useTopProximitySpy;